import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Mail,
  User as UserIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Stepper } from '@/components/ui/stepper';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import {
  useResetPasswordMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
} from '@/features/auth/api';
import {
  newPasswordSchema,
  resetOtpSchema,
  resetStartSchema,
  type NewPasswordInput,
  type ResetOtpInput,
  type ResetStartInput,
} from '@/features/auth/schema';

const STEPS = [
  { id: 'identify', label: 'Username', icon: UserIcon },
  { id: 'verify', label: 'Verify OTP', icon: Mail },
  { id: 'reset', label: 'New password', icon: KeyRound },
];

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState<string | null>(null);

  return (
    <AuthLayout
      title="Reset your password"
      description="Verify your identity with an OTP and choose a new password."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to sign in
        </Link>
      }
    >
      <Stepper steps={STEPS} current={step} className="-mt-2" />

      {step === 0 && (
        <IdentifyStep
          onNext={(input) => {
            setUsername(input.username);
            setStep(1);
          }}
        />
      )}

      {step === 1 && (
        <VerifyStep
          username={username}
          onBack={() => setStep(0)}
          onNext={({ userId: nextId }) => {
            setUserId(nextId);
            setStep(2);
          }}
        />
      )}

      {step === 2 && userId && (
        <ResetStep
          userId={userId}
          onDone={() => {
            toast.success('Password reset successfully. Please sign in.');
            navigate('/login', { replace: true });
          }}
        />
      )}
    </AuthLayout>
  );
}

/* -------------------- Step 1: enter username -------------------- */

function IdentifyStep({ onNext }: { onNext: (input: ResetStartInput) => void }) {
  const sendOtp = useSendOtpMutation();
  const form = useForm<ResetStartInput>({
    resolver: zodResolver(resetStartSchema),
    defaultValues: { username: '' },
  });

  const onSubmit = (input: ResetStartInput) => {
    sendOtp.mutate(
      { username: input.username.trim() },
      {
        onSuccess: () => {
          toast.success('OTP sent to your registered mobile number');
          onNext({ username: input.username.trim() });
        },
        onError: (err) => {
          form.setError('username', { message: readErrorMessage(err) ?? 'Could not send OTP' });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <FormField
          control={form.control}
          name="username"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Username</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoFocus
                  autoComplete="username"
                  placeholder="you@company.com"
                  disabled={sendOtp.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={sendOtp.isPending}>
          {sendOtp.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {sendOtp.isPending ? 'Sending OTP…' : 'Send OTP'}
        </Button>
      </form>
    </Form>
  );
}

/* -------------------- Step 2: enter OTP ------------------------ */

function VerifyStep({
  username,
  onBack,
  onNext,
}: {
  username: string;
  onBack: () => void;
  onNext: (payload: { userId: string }) => void;
}) {
  const verifyOtp = useVerifyOtpMutation();
  const sendOtp = useSendOtpMutation();
  const form = useForm<ResetOtpInput>({
    resolver: zodResolver(resetOtpSchema),
    defaultValues: { otp: '' },
  });

  const onSubmit = (input: ResetOtpInput) => {
    verifyOtp.mutate(
      { username, otp: input.otp.trim() },
      {
        onSuccess: (res) => onNext({ userId: res.userId }),
        onError: (err) => {
          form.setError('otp', { message: readErrorMessage(err) ?? 'Invalid OTP' });
        },
      },
    );
  };

  const resend = () => {
    sendOtp.mutate(
      { username },
      {
        onSuccess: () => toast.success('OTP re-sent'),
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not resend OTP'),
      },
    );
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Sent a code to the mobile number registered to{' '}
          <span className="font-medium text-foreground">{username}</span>.
        </div>

        <FormField
          control={form.control}
          name="otp"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>One-time code</FormLabel>
                <button
                  type="button"
                  onClick={resend}
                  disabled={sendOtp.isPending}
                  className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                >
                  {sendOtp.isPending ? 'Resending…' : 'Resend'}
                </button>
              </div>
              <FormControl>
                <Input
                  {...field}
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="Enter the code"
                  disabled={verifyOtp.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={verifyOtp.isPending}
            className="flex-1"
          >
            Back
          </Button>
          <Button type="submit" className="flex-1" disabled={verifyOtp.isPending}>
            {verifyOtp.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {verifyOtp.isPending ? 'Verifying…' : 'Verify'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

/* -------------------- Step 3: choose new password --------------- */

function ResetStep({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [showPassword, setShowPassword] = useState(false);
  const resetPwd = useResetPasswordMutation();
  const form = useForm<NewPasswordInput>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = (input: NewPasswordInput) => {
    resetPwd.mutate(
      { userId, password: input.password },
      {
        onSuccess: () => onDone(),
        onError: (err) => {
          form.setError('password', {
            message: readErrorMessage(err) ?? 'Password reset failed',
          });
        },
      },
    );
  };

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>New password</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input
                    {...field}
                    type={showPassword ? 'text' : 'password'}
                    autoFocus
                    autoComplete="new-password"
                    placeholder="••••••••"
                    disabled={resetPwd.isPending}
                    className="pr-9"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={resetPwd.isPending}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-50"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Confirm password</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  disabled={resetPwd.isPending}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <PasswordRules />

        {resetPwd.isError && !form.formState.errors.password && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{readErrorMessage(resetPwd.error) ?? 'Password reset failed'}</p>
          </div>
        )}

        <Button type="submit" className="w-full" disabled={resetPwd.isPending}>
          {resetPwd.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {resetPwd.isPending ? 'Saving…' : 'Reset password'}
        </Button>
      </form>
    </Form>
  );
}

function PasswordRules() {
  return (
    <ul className="grid grid-cols-1 gap-1 text-xs text-muted-foreground sm:grid-cols-2">
      <li className="flex items-center gap-1.5">
        <CheckCircle2 className="h-3 w-3 text-success" />
        At least 8 characters
      </li>
      <li className="flex items-center gap-1.5">
        <CheckCircle2 className="h-3 w-3 text-success" />
        At least one letter
      </li>
      <li className="flex items-center gap-1.5">
        <CheckCircle2 className="h-3 w-3 text-success" />
        At least one number
      </li>
      <li className="flex items-center gap-1.5">
        <Lock className="h-3 w-3" />
        Matches confirmation
      </li>
    </ul>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const maybeAxios = err as { response?: { data?: { message?: string } }; message?: string };
    return maybeAxios.response?.data?.message ?? maybeAxios.message;
  }
  if (typeof err === 'string') return err;
  return undefined;
}
